/**
 * Bridges the ELI chatbot and the billing screen.
 * When the user is already on /layout/Billing, automation runs in-place
 * instead of navigating away.
 */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

export interface BillingAutomationPayload {
  uhid: string | null;
  patientId?: number | null;
  patientName?: string | null;
  category?: string | null;
  doctor?: string | null;
  doctorId?: number | null;
  departmentId?: number | null;
  departmentName?: string | null;
  skipDoctor?: boolean;
  services?: string[];
  service?: string | null;
  serviceId?: number | null;
  mode: 'walkin' | 'appointment' | null;
  paymentMethod: string | null;
  /** FK_PayTypeId from Common/GetPaymenttype */
  paymentTypeId?: number | null;
  cashPaid?: boolean;
  remarks?: string | null;
  autoSubmit: boolean;
  autoPrint: boolean;
  /** Auto-create receipt after bill save (self-pay party 1). Default true. */
  autoReceipt?: boolean;
}

export interface BillSavedSummary {
  billId: number;
  billNo?: string;
  uhid: string | number;
  patientName: string;
  category: string;
  doctor: string;
  department?: string;
  services: string[];
  mode: string;
  paymentMethod: string;
  remarks?: string;
  netAmount?: number;
  totalAmount?: number;
  paidAmount?: number;
  printed: boolean;
  receiptCreated?: boolean;
  paymentTypeId?: number;
}

@Injectable({ providedIn: 'root' })
export class ChatbotAutomationService {
  private readonly automationSubject = new Subject<BillingAutomationPayload>();
  private readonly billSavedSubject  = new Subject<BillSavedSummary>();
  private readonly automationFailedSubject = new Subject<string>();
  /** Pre-opened on user click so async auto-print is not blocked by the popup blocker. */
  private reservedPrintWindow: Window | null = null;

  readonly automation$ = this.automationSubject.asObservable();
  readonly billSaved$  = this.billSavedSubject.asObservable();
  readonly automationFailed$ = this.automationFailedSubject.asObservable();

  constructor(private router: Router) {}

  /** Call synchronously from a user gesture (e.g. Confirm bill) before async billing. */
  reservePrintWindow(): void {
    this.releasePrintWindow();
    try {
      const win = window.open('', '_blank', 'top=0,left=0,height=100%,width=auto');
      if (win) {
        win.document.write('<html><body style="font-family:sans-serif;padding:16px">Preparing print…</body></html>');
        win.document.close();
        this.reservedPrintWindow = win;
      }
    } catch (_) {
      this.reservedPrintWindow = null;
    }
  }

  /** Returns a pre-opened window (if any) and clears the reservation. */
  consumePrintWindow(): Window | null {
    const win = this.reservedPrintWindow;
    this.reservedPrintWindow = null;
    if (win && !win.closed) {
      return win;
    }
    return null;
  }

  releasePrintWindow(): void {
    if (this.reservedPrintWindow && !this.reservedPrintWindow.closed) {
      try { this.reservedPrintWindow.close(); } catch (_) { /* ignore */ }
    }
    this.reservedPrintWindow = null;
  }

  isOnBillingPage(): boolean {
    const url = this.router.url || '';
    return /\/layout\/Billing(\/|$|\?)/i.test(url) || /\/Billing(\/|$|\?)/i.test(url);
  }

  /** Returns true if automation was dispatched in-place on the billing page. */
  triggerBillingAutomation(payload: BillingAutomationPayload): boolean {
    if (this.isOnBillingPage()) {
      this.automationSubject.next(payload);
      return true;
    }
    return false;
  }

  notifyBillSaved(summary: BillSavedSummary): void {
    this.billSavedSubject.next(summary);
  }

  notifyAutomationFailed(message: string): void {
    this.automationFailedSubject.next(message);
  }
}
